import { useAuth } from "@clerk/react";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { useWaitlist } from "../../hooks/useWaitlist";
import { getBrowserPathname, resolveClerkBypass } from "../../lib/dev-preview";
import { TARGET_CONFIRM_HREF } from "../../lib/target-confirm";
import {
  joinWaitlist,
  leaveWaitlist,
  type WaitlistPosition,
} from "../../lib/waitlist-api";
import { ReferralShareSheet } from "../referral/referral-share-sheet";
import { PageHeader } from "../ui/page-header";
import { useToast } from "../ui/toast";
import { MarketplaceHowItWorksLink } from "./MarketplaceHowItWorksLink";
import { joinAreaNames } from "./BrowseWaitlistState.helpers";
import { WaitlistPositionCard } from "./WaitlistPositionCard";

/**
 * Browse-agents surface for a buyer whose target area is NOT a launched metro
 * (plan 317 whitelist; backend `access: "area_not_launched"`). There is no
 * marketplace to browse there, so instead of an empty agent list we show the
 * waitlist / demand-map join surface:
 *   - already on the list (published auto-join OR explicit join) → the live
 *     position card (unlock / waiting / moved-up states);
 *   - not yet on the list (an unpublished buyer has no queued row) → an explicit
 *     "Join the waitlist" CTA, so raising your hand never requires publishing.
 *
 * The position card's refer CTA opens the shared `ReferralShareSheet` (same
 * minted link as Settings / the dashboard banner) — see
 * WaitlistPositionCardContainer.
 */
export interface BrowseWaitlistStateProps {
  areaLabel: string;
  /**
   * Code-review fix (M3 iteration 2, finding 8). Home's `AgentPreview`
   * mounts this component BESIDE `HomeHero`, which already renders its own
   * "Sill isn't in {area} yet" headline and Join-the-waitlist/Share-your-
   * link primary CTA for `area_not_launched`. `embedded` suppresses this
   * component's OWN duplicate headline + "Join the waitlist" button in the
   * not-yet-joined branch, so the buyer sees exactly one headline and one
   * join action, not two — the Home hero's primary CTA is the one that
   * joins. The already-joined `WaitlistPositionCard` branch is unaffected:
   * its position/share/leave content is never duplicated by the hero.
   * Defaults to `false` so the standalone `/marketplace/browse` page call
   * site (`BrowseAgentsPage`) is unchanged.
   */
  embedded?: boolean;
  /**
   * Display names of the metros Sill IS live in (the browse response's
   * `launched_areas`, present only at `area_not_launched` when the buyer's
   * TARGET ZIP is unlaunched — never for a search-override narrowing).
   * Drives the "Buying somewhere else? Sill is live in {areas}" block with
   * its "Change target area" link: a buyer whose target sits just outside a
   * launched metro's ZIP set must never read as locked out when a cheap
   * target-area edit would let them browse. Join stays the PRIMARY action so
   * the demand map still captures the hand-raise; this block is the
   * secondary path. Empty or absent renders nothing — a market name comes
   * only from the server.
   */
  launchedAreas?: readonly string[];
}

/**
 * Preview-safety (plan 435 M5, task:mfh-tabbar-320/mfh-preview-fixtures
 * fixture work surfaced this). `useWaitlist()` and this component's own
 * `getToken` both call Clerk's `useAuth()`, which throws outside a
 * `ClerkProvider`. Home's `AgentPreview` can mount this component on
 * `/preview/home?state=waitlisted` — a DEV preview path that bypasses
 * ClerkProvider entirely with no Clerk key (the no-key CI Playwright
 * posture; `main.tsx`'s own `shouldBypassClerk` decision). Branching at the
 * COMPONENT level — never inside a single hook body — keeps each branch's
 * hook calls unconditional (rules-of-hooks): `BrowseWaitlistStateAuthed`
 * calls `useWaitlist()`/`useAuth()` for real; the preview branch never
 * does, mirroring `DashboardShell`'s own `PreviewDashboardShellInner` /
 * `AuthedDashboardShellInner` fork for the identical reason. The check
 * reuses `resolveClerkBypass` verbatim (not a re-derived predicate) so this
 * only diverges from the authed path in the exact circumstance
 * `ClerkProvider` itself is actually absent — a real local Clerk key still
 * exercises the full join/leave flow on `/preview/home`, byte-identical to
 * before this fix.
 */
export function BrowseWaitlistState(props: BrowseWaitlistStateProps) {
  if (
    import.meta.env.DEV &&
    resolveClerkBypass(
      getBrowserPathname(),
      !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
    )
  ) {
    return <BrowseWaitlistStatePreview {...props} />;
  }
  return <BrowseWaitlistStateAuthed {...props} />;
}

function BrowseWaitlistStatePreview(props: BrowseWaitlistStateProps) {
  return (
    <BrowseWaitlistStateBody
      {...props}
      getToken={() => Promise.resolve(null)}
      refresh={async () => {
        /* no-op: no ClerkProvider mounted on this path */
      }}
      waitlist={null}
    />
  );
}

function BrowseWaitlistStateAuthed(props: BrowseWaitlistStateProps) {
  const { waitlist, refresh } = useWaitlist();
  const { getToken } = useAuth();
  return (
    <BrowseWaitlistStateBody
      {...props}
      getToken={getToken}
      refresh={refresh}
      waitlist={waitlist}
    />
  );
}

function BrowseWaitlistStateBody({
  areaLabel,
  embedded = false,
  launchedAreas,
  getToken,
  refresh,
  waitlist,
}: BrowseWaitlistStateProps & {
  getToken: () => Promise<string | null>;
  refresh: () => Promise<void>;
  waitlist: WaitlistPosition | null;
}) {
  const { pushToast } = useToast();
  const [joining, setJoining] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const liveAreas = joinAreaNames(launchedAreas);

  const onJoin = useCallback(async () => {
    setJoining(true);
    try {
      await joinWaitlist(getToken);
      await refresh();
      pushToast({ title: `You're on the waitlist for ${areaLabel}.` });
    } catch {
      pushToast({ title: "Couldn't join the waitlist — try again." });
    } finally {
      setJoining(false);
    }
  }, [getToken, refresh, pushToast, areaLabel]);

  const onLeave = useCallback(async () => {
    try {
      await leaveWaitlist(getToken);
      await refresh();
      pushToast({ title: "You've left the waitlist." });
    } catch {
      pushToast({ title: "Couldn't update the waitlist — try again." });
    }
  }, [getToken, refresh, pushToast]);

  return (
    // /marketplace/browse is a full-bleed workspace in DashboardShell (the agent
    // directory manages its own padding), so this standalone state supplies its
    // own page padding and centers a focused, max-width card in the frame.
    // Embedded in a Home block, the block supplies the layout — no page
    // padding, width cap, or centering (they left a tall gap on Home).
    <div
      className={
        embedded
          ? "space-y-5"
          : "mx-auto w-full max-w-xl space-y-5 px-4 py-8 text-center sm:px-5 lg:py-12"
      }
    >
      {/* Code-review fix (M3 iteration 2, finding 8; re-fix F8). `embedded`
          mounts this inside a Home block — a standalone page header reads
          wrong nested inside another page, so it's suppressed there. */}
      {!embedded ? (
        <PageHeader
          title="Browse agents"
          description={`Sill launches market by market. ${areaLabel} isn’t live yet — join the waitlist and we’ll prioritize it as more buyers raise their hand.`}
        />
      ) : null}
      {waitlist ? (
        <>
          <WaitlistPositionCard
            waitlist={waitlist}
            align="center"
            onShare={() => setShareOpen(true)}
            onLeave={() => void onLeave()}
          />
          <ReferralShareSheet
            open={shareOpen}
            onOpenChange={setShareOpen}
            waitlistArea={waitlist.area_label}
          />
        </>
      ) : embedded ? (
        <p
          className="marketplace-surface p-5 text-sm text-wrap-pretty text-muted-foreground"
          data-testid="browse-waitlist-embedded-note"
        >
          We’re adding markets carefully — join the waitlist above to be
          first in line when we open {areaLabel}.
        </p>
      ) : (
        <div className="marketplace-surface p-7 text-center">
          <h2 className="text-wrap-balance text-xl font-bold">
            Sill isn’t live in {areaLabel} yet
          </h2>
          <p className="mt-2 text-wrap-pretty text-sm text-muted-foreground">
            We’re adding markets carefully. Join the waitlist to be first in line
            when we open {areaLabel}.
          </p>
          <button
            className="mt-5 inline-flex min-h-11 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground"
            data-testid="browse-join-waitlist"
            onClick={() => void onJoin()}
            disabled={joining}
            type="button"
          >
            {joining ? "Joining…" : "Join the waitlist"}
          </button>
        </div>
      )}
      {liveAreas ? (
        <div
          className={
            embedded
              ? "marketplace-surface p-5 text-sm"
              : "marketplace-surface p-5 text-center text-sm"
          }
          data-testid="browse-waitlist-change-area"
        >
          <p className="font-semibold text-foreground">Buying somewhere else?</p>
          <p className="mt-1 text-wrap-pretty text-muted-foreground">
            Sill is live in {liveAreas}. Change your target area to browse
            those agents now. You’ll leave the {areaLabel} waitlist, and you
            can rejoin any time.
          </p>
          {/* Embedded on Home the hero's secondary CTA IS the action (the
              same one-action rule that suppresses the embedded Join button
              above), so only the standalone page carries its own link. */}
          {!embedded ? (
            <Link
              className="mt-3 inline-flex min-h-10 items-center rounded-full border border-border px-4 font-semibold text-foreground hover:bg-muted"
              data-testid="browse-waitlist-change-area-link"
              to={TARGET_CONFIRM_HREF}
            >
              Change target area
            </Link>
          ) : null}
        </div>
      ) : null}
      {/* M11-B3: one of the two entry points to the public how-it-works
          article — the buyer is deciding whether to raise their hand.
          `frontend/consumer/CLAUDE.md` scopes this link to exactly two
          surfaces (the publish confirm dialog and this standalone browse
          state) — the embedded-on-Home render is neither, so it's
          suppressed there (re-fix F8). */}
      {!embedded ? <MarketplaceHowItWorksLink surface="browse_waitlist" /> : null}
    </div>
  );
}
